import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PSelectItem } from '../../../../../shared/design-system/atoms/p-select/p-select-item.model';
import { Report } from '../../../../models/report.model';
import { getExposureLabel } from '../../../../services/mappers/exposition.mapper';

@Component({
  selector: 'app-maison-form',
  templateUrl: './maison-form.component.html',
  styleUrls: ['./maison-form.component.scss'],
})
export class MaisonFormComponent implements OnChanges {
  @Input() maison!: Report.Characteristics.Maison | undefined;
  @Input() constructionYear!: number;

  @Output() maisonChanged = new EventEmitter<Report.Characteristics.Maison>();

  @Output() constructionYearChanged = new EventEmitter<number>();

  maisonForm = new FormGroup({
    propertyType: new FormControl('Maison', { nonNullable: true }),
    constructionYear: new FormControl('', [
      Validators.min(1000),
      Validators.max(9999),
      Validators.pattern(/^\d{4}$/),
    ]),
    livingAreaM2: new FormControl(''),
    livingRoomAreaM2: new FormControl(''),
    landAreaM2: new FormControl(''),
    heating: new FormControl(''),
  });

  numberOfRoomsItem!: PSelectItem;
  numberOfBedroomsItem!: PSelectItem;
  numberOfBathroomsItem!: PSelectItem;
  levelItem!: PSelectItem;
  exposureItem!: PSelectItem;
  ghgEmissionsItem!: PSelectItem;
  energyPerformanceItem!: PSelectItem;

  numberSelectItems: PSelectItem[] = [
    { value: null, label: '' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10 et +', label: '10 et +' },
  ];

  exposureSelectItems: PSelectItem[] = [
    { value: null, label: '' },
    { value: 'N', label: 'Nord' },
    { value: 'S', label: 'Sud' },
    { value: 'E', label: 'Est' },
    { value: 'O', label: 'Ouest' },
    { value: 'NE', label: 'Nord-Est' },
    { value: 'SE', label: 'Sud-Est' },
    { value: 'NO', label: 'Nord-Ouest' },
    { value: 'SO', label: 'Sud-Ouest' },
  ];

  alphabiticalCategoriesItems: PSelectItem[] = [
    { value: null, label: '' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' },
    { value: 'F', label: 'F' },
    { value: 'G', label: 'G' },
  ];

  maisonInternal!: Report.Characteristics.Maison;
  constructionYearinternal!: number;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maison'] && changes['maison'].currentValue) {
      this.maisonInternal = changes['maison'].currentValue;
    }

    if (
      changes['constructionYear'] &&
      changes['constructionYear'].currentValue
    ) {
      this.maisonForm.controls.constructionYear.setValue(
        this.constructionYear.toString(),
      );
    }

    this.setFormValues();
  }

  onConstructionYearChanged(event: Event) {
    this.constructionYearinternal = parseInt(
      (event.target as HTMLInputElement).value,
    );

    this.constructionYearChanged.emit(this.constructionYearinternal);
  }

  onLivingAreaM2Changed(event: Event) {
    this.maisonInternal = {
      ...this.maisonInternal,
      livingAreaM2: parseInt((event.target as HTMLInputElement).value),
    };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onLivingRoomAreaM2Changed(event: Event) {
    this.maisonInternal = {
      ...this.maisonInternal,
      livingRoomAreaM2: parseInt((event.target as HTMLInputElement).value),
    };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onLandAreaM2Changed(event: Event) {
    this.maisonInternal = {
      ...this.maisonInternal,
      landAreaM2: parseInt((event.target as HTMLInputElement).value),
    };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onHeatingChanged(event: Event) {
    this.maisonInternal = {
      ...this.maisonInternal,
      heating: (event.target as HTMLInputElement).value,
    };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onExposureSelected({ value }: PSelectItem): void {
    this.maisonInternal = {
      ...this.maisonInternal,
      exposure: value ? value : null,
    };

    this.exposureItem = { value };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onNumberOfRoomsSelected({ value }: PSelectItem): void {
    this.maisonInternal = {
      ...this.maisonInternal,
      numberOfRooms: value ? value : null,
    };

    this.numberOfRoomsItem = { value };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onNumberOfBedroomsSelected({ value }: PSelectItem): void {
    this.maisonInternal = {
      ...this.maisonInternal,
      numberOfBedrooms: value ? value : null,
    };

    this.numberOfBedroomsItem = { value };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onNumberOfBathroomsSelected({ value }: PSelectItem): void {
    this.maisonInternal = {
      ...this.maisonInternal,
      numberOfBathrooms: value ? value : null,
    };

    this.numberOfBathroomsItem = { value };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onGhgEmissionsSelected({ value }: PSelectItem): void {
    this.maisonInternal = {
      ...this.maisonInternal,
      ghgEmissions: value ? value : null,
    };

    this.ghgEmissionsItem = { value };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onEnergyPerformanceSelected({ value }: PSelectItem): void {
    this.maisonInternal = {
      ...this.maisonInternal,
      energyPerformance: value ? value : null,
    };

    this.energyPerformanceItem = { value };

    this.maisonChanged.emit(this.maisonInternal);
  }

  onLevelSelected({ value }: PSelectItem): void {
    this.maisonInternal = {
      ...this.maisonInternal,
      level: value ? value : null,
    };

    this.levelItem = { value };

    this.maisonChanged.emit(this.maisonInternal);
  }

  setFormValues(): void {
    this.maisonForm.controls.constructionYear.setValue(
      this.constructionYearinternal
        ? this.constructionYearinternal.toString()
        : '',
    );

    this.maisonForm.controls.livingAreaM2.setValue(
      this.maisonInternal?.livingAreaM2?.toString() || '',
    );

    this.maisonForm.controls.livingRoomAreaM2.setValue(
      this.maisonInternal?.livingRoomAreaM2?.toString() || '',
    );

    this.maisonForm.controls.landAreaM2.setValue(
      this.maisonInternal?.landAreaM2?.toString() || '',
    );

    this.maisonForm.controls.heating.setValue(
      this.maisonInternal?.heating || '',
    );

    this.numberOfRoomsItem = {
      value:
        this.maisonInternal?.numberOfRooms || this.numberSelectItems[0].value,
    };

    this.numberOfBedroomsItem = {
      value:
        this.maisonInternal?.numberOfBedrooms ||
        this.numberSelectItems[0].value,
    };

    this.numberOfBathroomsItem = {
      value:
        this.maisonInternal?.numberOfBathrooms ||
        this.numberSelectItems[0].value,
    };

    this.ghgEmissionsItem = {
      value:
        this.maisonInternal?.ghgEmissions ||
        this.alphabiticalCategoriesItems[0].value,
    };

    this.levelItem = {
      value: this.maisonInternal?.level || this.numberSelectItems[0].value,
    };

    this.exposureItem = {
      value: this.maisonInternal?.exposure || this.exposureSelectItems[0].value,
      label:
        getExposureLabel(this.maisonInternal?.exposure) ||
        this.exposureSelectItems[0].label,
    };

    this.energyPerformanceItem = {
      value:
        this.maisonInternal?.energyPerformance ||
        this.alphabiticalCategoriesItems[0].value,
    };

    if (!this.maisonInternal.numberOfRooms) {
      this.maisonInternal.numberOfRooms = this.numberSelectItems[0]
        .value as string;
    }

    if (!this.maisonInternal.numberOfBedrooms) {
      this.maisonInternal.numberOfBedrooms = this.numberSelectItems[0]
        .value as string;
    }

    if (!this.maisonInternal.numberOfBathrooms) {
      this.maisonInternal.numberOfBathrooms = this.numberSelectItems[0]
        .value as string;
    }

    if (!this.maisonInternal.exposure) {
      this.maisonInternal.exposure = this.exposureSelectItems[0]
        .value as string;
    }

    if (!this.maisonInternal.level) {
      this.maisonInternal.level = this.numberSelectItems[0].value as string;
    }

    if (!this.maisonInternal.ghgEmissions) {
      this.maisonInternal.ghgEmissions = this.alphabiticalCategoriesItems[0]
        .value as string;
    }

    if (!this.maisonInternal.energyPerformance) {
      this.maisonInternal.energyPerformance = this
        .alphabiticalCategoriesItems[0].value as string;
    }
  }
}
